/* eslint-disable no-unused-vars */
import React, { useState, useMemo } from "react";
import { Card, Row, Col, Radio, Tooltip, message, Table } from "antd";
import { PieChartOutlined, TableOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import { HubCPOverviewService } from "../../../services/api/hubCPOverviewService";
import ShipmentDetailsModal from "../ShipmentDetailsModal";
import "./index.scss";

// Status to Operation Code mapping
const STATUS_TO_OPERATION_CODE = {
  "INSCANNED_AT_CP": 9,
  "OUT FOR DELIVERY": 11,
  "HUB-IN-SCANNED_SHIPMENTS": 6,
  "IN-BAG-INSCAN": 40,
  "INSCAN": 3,
  "INSCANNED_AT_TRANSIT_SHIPMENTS": 20,
  "IN-BAG-OUTSCAN": 41,
  "BAGGED": 21,
  "RTO_OUT_FOR_DELIVERY": 45,
  "RETURN_INITIATED": 15,
  "HUB-IN-SCANNED_BAGS": 6,
  "FORCE_BAG_ATTEMPTED": 54,
  "OUTSCAN_BAG": 5,
  "INSCANNED_AT_TRANSIT_BAGS": 20,
  "UNDELIVERED": 55,
  "OUTSCAN_SHIPMENT": 5,
  "OUTSCAN_TO_CP": 7,
  "IN_BAG_OUTSCAN_TO_CP": 48,
};

const StatusDistribution = ({ data = [], selectedHubs = null, areAllHubsSelected = false }) => {
  const [viewMode, setViewMode] = useState("chart");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0 });

  // Use flat data from new API
  const currentData = Array.isArray(data) ? data : [];
  const grandTotal = currentData.reduce((sum, item) => sum + item.count, 0);

  // Color palette for statuses
  const getStatusColor = (status, index) => {
    const colorMap = {
      "INSCAN": "#2e77d0",
      "OUTSCAN": "#52c41a",
      "IN-BAG-INSCAN": "#faad14",
      "UNDELIVERED": "#ff4d4f",
      "RETURN": "#ff7875",
      "OUT FOR DELIVERY": "#13c2c2",
      "RETURN_INITIATED": "#ff9c6e",
      "INSCANNED_AT_TRANSIT": "#9254de",
      "FORCE_BAG_ATTEMPTED": "#eb2f96",
      "HUB-IN-SCANNED": "#1890ff",
      "IN-BAG-OUTSCAN": "#36cfc9",
      "INSCANNED_AT_CP": "#73d13d",
      "INSCANNED": "#bae637",
      "IN_BAG_OUTSCAN_TO_CP": "#ffc53d",
      "RTO_OUT_FOR_DELIVERY": "#ff85c0",
      "BAGGED": "#adc6ff",
    };
    return colorMap[status] || `hsl(${(index * 137.5) % 360}, 70%, 50%)`;
  };

  // Calculate pie chart data
  const pieData = useMemo(() => {
    if (!currentData || currentData.length === 0 || grandTotal === 0) {
      return [];
    }
    return currentData
      .filter((item) => item.count > 0)
      .map((item, index) => {
        const percentage = (item.count / grandTotal) * 100;
        return {
          ...item,
          percentage: percentage.toFixed(1),
          color: getStatusColor(item.status, index),
          index,
        };
      })
      .sort((a, b) => b.count - a.count);
  }, [currentData, grandTotal]);

  // Calculate SVG pie chart paths
  const piePaths = useMemo(() => {
    if (!pieData || pieData.length === 0 || grandTotal === 0) {
      return [];
    }

    const radius = 120;
    const centerX = 150;
    const centerY = 150;
    let currentAngle = -90; // Start from top

    return pieData.map((item) => {
      const angle = (item.count / grandTotal) * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;

      const x1 = centerX + radius * Math.cos((startAngle * Math.PI) / 180);
      const y1 = centerY + radius * Math.sin((startAngle * Math.PI) / 180);
      const x2 = centerX + radius * Math.cos((endAngle * Math.PI) / 180);
      const y2 = centerY + radius * Math.sin((endAngle * Math.PI) / 180);

      const largeArcFlag = angle > 180 ? 1 : 0;

      const pathData = [
        `M ${centerX} ${centerY}`,
        `L ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        "Z",
      ].join(" ");

      currentAngle = endAngle;

      return {
        ...item,
        pathData,
        startAngle,
        endAngle,
      };
    });
  }, [pieData, grandTotal]);

  // Fetch clickable data with pagination
  const fetchClickableData = async (status, pageNum = 1, pageSize = 5) => {
    try {
      // If all hubs are selected, pass null; otherwise use the selected hub
      const hubIdToUse = areAllHubsSelected ? null : (selectedHubs || null);

      if (!areAllHubsSelected && !hubIdToUse) {
        message.warning("Please select a hub");
        return;
      }

      // Get operation code from status name
      const operationCode = STATUS_TO_OPERATION_CODE[status.status];



      // Build dateFilter based on selectedPeriod - use "tillDate" as default for now
      const dateFilter = "tillDate";

      setModalLoading(true);

      // Use new API for pie chart clickable report
      const response = await HubCPOverviewService.getPieChartHubManagerOverviewClickable({
        currentHubId: hubIdToUse,
        status: status.status,
        page: pageNum - 1,
        size: pageSize,
      });

      if (response && response.data) {
        setModalData(response.data);
        setTotalCount(response.count || 0); // Note: API response uses 'count'
        setPagination(prev => ({
          ...prev,
          current: pageNum,
          pageSize: pageSize,
          total: response.count || 0
        }));
      }
    } catch (error) {
      message.error(error?.message || "Failed to fetch shipment details");
      console.error("Error fetching clickable data:", error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleStatusClick = async (status) => {
    setSelectedStatus(status);
    setDetailsVisible(true);
    setModalData([]);
    setPagination({ current: 1, pageSize: 5, total: 0 });
    await fetchClickableData(status, 1, 5);
  };

  // Handle modal table pagination
  const handleTableChange = (newPagination) => {
    const { current, pageSize } = newPagination;
    setPagination(prev => ({
      ...prev,
      current: current,
      pageSize: pageSize
    }));
    if (selectedStatus) {
      fetchClickableData(selectedStatus, current, pageSize);
    }
  };

  // Table columns for table view
  const tableColumns = [
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 200,
      render: (text, record) => (
        <div className="status-cell">
          <span
            className="status-color-indicator"
            style={{ backgroundColor: record.color }}
          />
          <span className="status-name">{text}</span>
        </div>
      ),
    },
    {
      title: "Count",
      dataIndex: "count",
      key: "count",
      width: 120,
      align: "right",
      render: (count, record) => (
        <span
          className="count-link"
          style={{ cursor: "default", color: "inherit", textDecoration: "none" }}
        // onClick={() => handleStatusClick(record)}
        >
          {count.toLocaleString()}
        </span>
      ),
    },
    {
      title: "Percentage",
      dataIndex: "percentage",
      key: "percentage",
      width: 120,
      align: "right",
      render: (percentage) => `${percentage}%`,
    },
  ];


  return (
    <>
      <Card className="status-distribution-card">
        <div className="status-distribution-header">
          <h3 className="card-title">Status Distribution</h3>
          <Radio.Group
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            size="small"
            className="view-toggle"
          >
            <Radio.Button value="chart">
              <PieChartOutlined /> Chart
            </Radio.Button>
            <Radio.Button value="table">
              <TableOutlined /> Table
            </Radio.Button>
          </Radio.Group>
        </div>

        <div className="grand-total">
          <span className="total-label">Grand Total:</span>
          <span className="total-value">{grandTotal.toLocaleString()}</span>
        </div>

        {currentData.length === 0 || grandTotal === 0 ? (
          <div className="no-data-message">
            <p>No data available for the selected period</p>
          </div>
        ) : viewMode === "chart" ? (
          <Row gutter={[16, 16]} className="chart-view">
            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
              <div className="pie-chart-container">
                <svg width="280" height="280" viewBox="0 0 300 300" className="pie-chart">
                  {piePaths.map((item, index) => (
                    <Tooltip
                      key={item.status}
                      title={`${item.status}: ${item.count.toLocaleString()} (${item.percentage}%)`}
                    >
                      <path
                        d={item.pathData}
                        fill={item.color}
                        stroke="#fff"
                        strokeWidth="2"
                        className="pie-segment"
                        onClick={() => handleStatusClick(item)}
                        style={{
                          cursor: "pointer",
                          opacity: 1, // disabled opacity effect
                          // opacity: selectedStatus?.status === item.status ? 1 : 0.8,
                          transform: "scale(1)",
                          // transform: selectedStatus?.status === item.status ? "scale(1.05)" : "scale(1)",
                          transformOrigin: "150px 150px",
                          transition: "all 0.3s ease",
                        }}
                      // onMouseEnter={(e) => {
                      //   e.target.style.opacity = "1";
                      //   e.target.style.transform = "scale(1.05)";
                      // }}
                      // onMouseLeave={(e) => {
                      //   if (selectedStatus?.status !== item.status) {
                      //     e.target.style.opacity = "0.8";
                      //     e.target.style.transform = "scale(1)";
                      //   }
                      // }}
                      />
                    </Tooltip>
                  ))}
                </svg>
                <div className="chart-legend">
                  {pieData.map((item) => (
                    <div
                      key={item.status}
                      className="legend-item"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleStatusClick(item)}
                    >
                      <span
                        className="legend-color"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="legend-text">
                        {item.status} ({item.count.toLocaleString()})
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Col>
          </Row>
        ) : (
          <div className="table-view">
            <Table
              columns={tableColumns}
              dataSource={pieData.map((item, index) => ({ ...item, key: index }))}
              pagination={false}
              size="middle"
              className="status-table"
            />
          </div>
        )}
      </Card>

      <ShipmentDetailsModal
        visible={detailsVisible}
        onCancel={() => {
          setDetailsVisible(false);
          setModalData([]);
          setSelectedStatus(null);
          setPagination({ current: 1, pageSize: 5, total: 0 });
        }}
        title={selectedStatus ? `${selectedStatus.status} - Details` : "Shipment Details"}
        data={modalData}
        loading={modalLoading}
        pagination={pagination}
        onTableChange={handleTableChange}
        totalCount={totalCount}
        summaryData={
          selectedStatus
            ? {
              count: selectedStatus.count,
              percentage: selectedStatus.percentage,
            }
            : null
        }
        operationCodes={
          selectedStatus
            ? STATUS_TO_OPERATION_CODE[selectedStatus.status]
            : null
        }
        hubId={areAllHubsSelected ? null : (selectedHubs || null)}
        // For new API, we might not need operationCodes or dateFilter, but keeping them if needed or can pass null
        // Passing isPieChart status to modal to trigger correct download function if needed
        isPieChart={true}
        status={selectedStatus?.status}
      />
    </>
  );
};

StatusDistribution.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    status: PropTypes.string,
    count: PropTypes.number
  })),
  selectedHubs: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  areAllHubsSelected: PropTypes.bool,
};

export default StatusDistribution;
